import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'antd';

const detailStyles = `
.pod-section-title { font-size:11px; font-weight:700; color:#1890ff; letter-spacing:1px; text-transform:uppercase; margin:16px 0 10px; display:flex; align-items:center; gap:6px; }

/* Descriptions replacement */
.pod-desc { display:grid; grid-template-columns:repeat(3,1fr); gap:0; border:1px solid #e6f0ff; border-radius:8px; overflow:hidden; margin-bottom:14px; }
@media(max-width:600px){ .pod-desc { grid-template-columns:1fr; } }
.pod-desc-item { padding:10px 14px; border-right:1px solid #e6f0ff; }
.pod-desc-item:last-child { border-right:none; }
.pod-desc-label { font-size:10px; font-weight:700; color:#8c8c8c; letter-spacing:.6px; text-transform:uppercase; margin-bottom:3px; }
.pod-desc-value { font-size:13px; font-weight:600; color:#262626; }

/* Tables */
.pod-wrap { overflow-x:auto; margin-bottom:14px; }
.pod-table { width:100%; border-collapse:collapse; font-size:12px; }
.pod-table thead tr { background:#f0f7ff; }
.pod-table thead th { padding:8px 12px; font-size:10px; font-weight:700; color:#1890ff; letter-spacing:1px; text-transform:uppercase; border:1px solid #e6f0ff; white-space:nowrap; text-align:center; }
.pod-table tbody td { padding:8px 12px; border:1px solid #e6f0ff; font-size:12px; color:#262626; font-weight:500; vertical-align:middle; }
.pod-table tbody tr:nth-child(even) td { background:#f7f9ff; }
.pod-table tbody tr:hover td { background:#e6f7ff!important; }

/* Match item highlight */
.pod-match td { background:#bae7ff!important; }
.pod-match:hover td { background:#91d5ff!important; }

/* Total row */
.pod-total td { background:#f0f7ff!important; font-weight:700!important; color:#0050b3!important; border-top:2px solid #d6e8ff!important; }

/* Empty */
.pod-empty td { text-align:center; color:#8c8c8c; font-style:italic; padding:20px!important; }

/* Amount */
.pod-amount { font-weight:700; color:#0050b3; font-family:monospace; }
.pod-budget { font-weight:600; color:#d46b08; font-family:monospace; }

/* Match badge */
.pod-match-badge { display:inline-flex; align-items:center; gap:3px; padding:1px 7px; border-radius:8px; background:#bae7ff; border:1px solid #91d5ff; font-size:10px; font-weight:700; color:#096dd9; }
`;

const PoDetails = ({ poDetails, poItems, poHistory }) => (
  <>
    <style>{detailStyles}</style>

    {/* Customer PO Info */}
    <div className="pod-desc">
      <div className="pod-desc-item">
        <div className="pod-desc-label">Customer Name</div>
        <div className="pod-desc-value">{poDetails.customerName || '—'}</div>
      </div>
      <div className="pod-desc-item">
        <div className="pod-desc-label">Purchase Order</div>
        <div className="pod-desc-value" style={{ color: '#0050b3' }}>{poDetails.poNumber || '—'}</div>
      </div>
      <div className="pod-desc-item">
        <div className="pod-desc-label">Date</div>
        <div className="pod-desc-value">{poDetails.poDate || '—'}</div>
      </div>
    </div>

    {/* PO Items Table */}
    <div className="pod-wrap">
      <table className="pod-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Item Description</th>
            <th>Quantity</th>
            <th>Currency</th>
            <th>Unit Price</th>
            <th>Budget Price</th>
            <th>Amount</th>
            <th>Budget Price Amount</th>
          </tr>
        </thead>
        <tbody>
          {poItems.length === 0 ? (
            <tr className="pod-empty">
              <td colSpan="8">
                <Icon type="inbox" style={{ fontSize: 20, display: 'block', margin: '0 auto 4px' }} />
                NO RECORD
              </td>
            </tr>
          ) : (
            <>
              {poItems.map(item => (
                <tr key={item.id} className={item.isMatchItem ? 'pod-match' : ''}>
                  <td>
                    {item.code}
                    {item.isMatchItem && <span className="pod-match-badge" style={{ marginLeft: 4 }}>MATCH</span>}
                  </td>
                  <td>{item.itemDescription}</td>
                  <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                  <td style={{ textAlign: 'center' }}>{item.currency}</td>
                  <td style={{ textAlign: 'right' }}>{item.unitPrice}</td>
                  <td style={{ textAlign: 'right' }}><span className="pod-budget">{item.budgetPrice}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="pod-amount">{item.totalAmount.toFixed(2)}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="pod-budget">{item.budgetPriceTotal.toFixed(2)}</span></td>
                </tr>
              ))}
              <tr className="pod-total">
                <td colSpan="6" />
                <td style={{ textAlign: 'right' }}>
                  {poItems.reduce((acc, val) => acc + val.totalAmount, 0).toFixed(2)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {poItems.reduce((acc, val) => acc + val.budgetPriceTotal, 0).toFixed(2)}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>

    {/* History section */}
    <div className="pod-section-title">
      <Icon type="history" />
      Raw Materials Purchased History
    </div>

    <div className="pod-wrap">
      <table className="pod-table">
        <thead>
          <tr>
            <th>Supplier</th>
            <th>Job Order/s</th>
            <th>Purchase Request/s</th>
            <th>Purchase Order</th>
            <th>Item Description/s</th>
            <th>Total Amount</th>
            <th>Sent to Supplier</th>
          </tr>
        </thead>
        <tbody>
          {poHistory.length === 0 ? (
            <tr className="pod-empty">
              <td colSpan="7">
                <Icon type="inbox" style={{ fontSize: 20, display: 'block', margin: '0 auto 4px' }} />
                NO RECORD
              </td>
            </tr>
          ) : (
            <>
              {poHistory.map(history => (
                <tr key={history.poNumber}>
                  <td>{history.supplierName}</td>
                  <td>{history.joNumber}</td>
                  <td>{history.prNumbers}</td>
                  <td style={{ color: '#0050b3', fontWeight: 700 }}>{history.poNumber}</td>
                  <td>{history.itemDescription}</td>
                  <td style={{ textAlign: 'right' }}><span className="pod-amount">{history.totalAmount.toFixed(2)}</span></td>
                  <td style={{ textAlign: 'center' }}>{history.isSent}</td>
                </tr>
              ))}
              <tr className="pod-total">
                <td colSpan="5" />
                <td style={{ textAlign: 'right' }}>
                  {poHistory.reduce((acc, val) => acc + val.totalAmount, 0).toFixed(2)}
                </td>
                <td />
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  </>
);

PoDetails.propTypes = {
  poDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  poItems:   PropTypes.arrayOf(PropTypes.object).isRequired,
  poHistory: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default PoDetails;